// Feature: features/super-admin-dashboard.md
import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Flex,
  SimpleGrid,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Select,
  Spinner,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  HStack,
  VStack,
  Divider
} from '@chakra-ui/react';
import { RepeatIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import { AppContext } from './AppContext';
import SuperAdminOrgPulseView, {
  formatShortDate,
  formatRelativeTime,
  formatTimestamp,
  getRateColorScheme
} from './SuperAdminOrgPulseView';
import SuperAdminErrorsTriagePanel from './SuperAdminErrorsTriagePanel';
import SuperAdminUserActivityPanel from './SuperAdminUserActivityPanel';

const DEFAULT_DAYS_WINDOW = 5;
const ORG_TAB_INDEX = { NMA: 2, USN: 3, MPH: 4 };
const RECENT_LOGINS_TAB_INDEX = 5;

const SuperAdminDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();

  const [daysWindow, setDaysWindow] = useState(DEFAULT_DAYS_WINDOW);
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  const fetchOverview = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);

    try {
      const response = await callApi(
        `/admin/super-dashboard/overview?days=${daysWindow}`,
        accessToken,
        { method: 'GET' }
      );
      if (response.status === 403) {
        setError('Super-Admin permissions are required to view this dashboard.');
        return;
      }
      if (!response.ok) {
        throw new Error('Failed to load Super-Admin multi-org overview.');
      }
      const data = await response.json();
      setOverviewData(data);
    } catch (err) {
      console.error('Error fetching Super-Admin overview:', err);
      setError(err.message || 'Failed to load dashboard metrics.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, daysWindow]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const orgs = overviewData?.orgs || {};
  const allDiagnostics = overviewData?.all_error_diagnostics || [];
  const orgKeys = ['NMA', 'USN', 'MPH'];

  const totals = orgKeys.reduce(
    (acc, key) => {
      const s = orgs[key]?.summary || {};
      acc.created += s.total_created || 0;
      acc.coded += s.total_coded || 0;
      acc.uncoded += s.total_uncoded || 0;
      acc.users += s.active_users_count || 0;
      acc.syncErrors += (s.sync_failed_count || 0) + (s.upload_failed_count || 0);
      return acc;
    },
    { created: 0, coded: 0, uncoded: 0, users: 0, syncErrors: 0 }
  );
  const globalCodingRate = totals.created > 0 ? Math.round((totals.coded / totals.created) * 1000) / 10 : 0;

  const totals24h = overviewData?.last_24h_totals || orgKeys.reduce(
    (acc, key) => {
      const l24 = orgs[key]?.last_24h || {};
      acc.total_created += l24.total_created || 0;
      acc.total_coded += l24.total_coded || 0;
      acc.total_uncoded += l24.total_uncoded || 0;
      acc.total_failures += l24.total_failures || 0;
      acc.active_users_count += l24.active_users_count || 0;
      return acc;
    },
    {
      total_created: 0,
      total_coded: 0,
      total_uncoded: 0,
      total_failures: 0,
      active_users_count: 0
    }
  );

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Box maxW="container.xl" mx="auto" py={8} px={4}>
        <Flex
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap={4}
          mb={6}
        >
          <Box>
            <HStack spacing={3}>
              <Heading size="lg" color="gray.800">
                Super-Admin System Pulse
              </Heading>
              <Badge colorScheme="purple" fontSize="xs" px={2} py={1} borderRadius="md">
                SUPER-ADMIN ONLY
              </Badge>
            </HStack>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Cross-organization highlights, uncoded & integration error diagnostics, and user login activity
            </Text>
          </Box>

          <HStack spacing={3} flexWrap="wrap">
            <Button
              size="sm"
              rightIcon={<ExternalLinkIcon />}
              colorScheme="purple"
              variant="solid"
              onClick={() => navigate('/super-admin-dashboard/billing')}
            >
              Billing Command Center
            </Button>
            <Select
              size="sm"
              w="150px"
              bg="white"
              shadow="xs"
              value={daysWindow}
              onChange={(e) => setDaysWindow(Number(e.target.value))}
            >
              <option value={3}>Last 3 Days</option>
              <option value={5}>Last 5 Days</option>
              <option value={7}>Last 7 Days</option>
              <option value={14}>Last 14 Days</option>
              <option value={30}>Last 30 Days</option>
            </Select>
            <Button
              size="sm"
              leftIcon={<RepeatIcon />}
              colorScheme="blue"
              variant="outline"
              bg="white"
              onClick={fetchOverview}
              isLoading={loading}
            >
              Refresh
            </Button>
          </HStack>
        </Flex>

        {error && (
          <Alert status="error" borderRadius="md" mb={6}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Tabs
          colorScheme="blue"
          variant="enclosed"
          isLazy
          index={tabIndex}
          onChange={(idx) => setTabIndex(idx)}
        >
          <TabList flexWrap="wrap">
            <Tab fontWeight="bold">Overview (All Orgs)</Tab>
            <Tab fontWeight="bold">
              Errors & Uncoded
              <Badge ml={2} colorScheme={allDiagnostics.length > 0 ? 'red' : 'green'}>
                {allDiagnostics.length}
              </Badge>
            </Tab>
            <Tab fontWeight="semibold">NMA (Org 1)</Tab>
            <Tab fontWeight="semibold">USNeuro (Org 155)</Tab>
            <Tab fontWeight="semibold">MPowerHealth (Org 203)</Tab>
            <Tab fontWeight="bold">
              Recent Logins & Activity
              <Badge ml={2} colorScheme="blue">
                All Orgs
              </Badge>
            </Tab>
          </TabList>

          <TabPanels>
            {/* Tab 0: Main Cross-Org Overview */}
            <TabPanel px={0} pt={6}>
              {loading && !overviewData ? (
                <Flex justify="center" align="center" py={16}>
                  <Spinner size="xl" color="blue.500" />
                </Flex>
              ) : (
                <>
                  {/* Top Section: Last 24 Hours Pulse by Org (Rolling 24h) */}
                  <Box
                    mb={8}
                    p={4}
                    bg="white"
                    border="1px solid"
                    borderColor="blue.200"
                    borderRadius="lg"
                    shadow="sm"
                  >
                    <Flex
                      justify="space-between"
                      align={{ base: 'flex-start', md: 'center' }}
                      direction={{ base: 'column', md: 'row' }}
                      gap={3}
                      mb={4}
                    >
                      <Box>
                        <HStack spacing={2.5}>
                          <Heading size="md" color="gray.800">
                            Last 24 Hours Pulse (By Org)
                          </Heading>
                          <Badge colorScheme="blue" variant="solid" fontSize="2xs" px={2} py={0.5} borderRadius="full">
                            ROLLING 24H
                          </Badge>
                        </HStack>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                          Real-time 24-hour snapshot of case ingestion, total coded, failures, and customer user activity
                        </Text>
                      </Box>

                      <HStack spacing={2} flexWrap="wrap">
                        <Badge colorScheme="gray" fontSize="xs" px={2.5} py={1} borderRadius="md">
                          24h Created: {totals24h.total_created}
                        </Badge>
                        <Badge colorScheme="green" fontSize="xs" px={2.5} py={1} borderRadius="md">
                          24h Coded: {totals24h.total_coded}
                        </Badge>
                        <Badge
                          colorScheme={totals24h.total_uncoded > 0 ? 'orange' : 'green'}
                          fontSize="xs"
                          px={2.5}
                          py={1}
                          borderRadius="md"
                        >
                          24h Uncoded: {totals24h.total_uncoded}
                        </Badge>
                        <Badge
                          colorScheme={totals24h.total_failures > 0 ? 'red' : 'green'}
                          fontSize="xs"
                          px={2.5}
                          py={1}
                          borderRadius="md"
                          cursor="pointer"
                          onClick={() => setTabIndex(1)}
                        >
                          24h Failures: {totals24h.total_failures}
                        </Badge>
                        <Badge
                          colorScheme="blue"
                          fontSize="xs"
                          px={2.5}
                          py={1}
                          borderRadius="md"
                          cursor="pointer"
                          onClick={() => setTabIndex(RECENT_LOGINS_TAB_INDEX)}
                        >
                          24h Active Users: {totals24h.active_users_count}
                        </Badge>
                      </HStack>
                    </Flex>

                    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
                      {orgKeys.map((key) => {
                        const pulse = orgs[key] || {};
                        const l24 = pulse.last_24h || {};
                        const created24h = l24.total_created || 0;
                        const coded24h = l24.total_coded || 0;
                        const codedAny24h = l24.coded_any_cohort || 0;
                        const cptCoded24h = l24.total_cpt_coded || 0;
                        const uncoded24h = l24.total_uncoded || 0;
                        const failures24h = l24.total_failures || 0;
                        const coreFailed24h = l24.core_failed_count || 0;
                        const syncFailed24h = l24.sync_failed_count || 0;
                        const uploadFailed24h = l24.upload_failed_count || 0;
                        const fileErr24h = l24.file_error_count || 0;
                        const rate24h = l24.coding_rate || 0;
                        const denom24h = pulse.is_async_coding_org ? coded24h + uncoded24h : created24h;
                        const rateScheme24h = getRateColorScheme(rate24h, denom24h);
                        const logins24h = (l24.recent_logins || []).slice(0, 3);

                        const hasFailures = failures24h > 0;
                        const hasUncoded = uncoded24h > 0;

                        return (
                          <Card
                            key={`24h-${key}`}
                            variant="outline"
                            borderColor={hasFailures ? 'red.300' : hasUncoded ? 'orange.200' : 'gray.200'}
                            bg={hasFailures ? 'red.50' : 'gray.50'}
                          >
                            <CardHeader pb={2} pt={3} px={4}>
                              <Flex justify="space-between" align="center">
                                <HStack spacing={2}>
                                  <Heading size="sm" color="gray.800">
                                    {pulse.org_name || key}
                                  </Heading>
                                  <Badge colorScheme="gray" fontSize="2xs">
                                    Org {pulse.org_id}
                                  </Badge>
                                  <Badge
                                    colorScheme={hasFailures ? 'red' : hasUncoded ? 'orange' : 'green'}
                                    fontSize="2xs"
                                  >
                                    {hasFailures
                                      ? `${failures24h} Failure${failures24h === 1 ? '' : 's'}`
                                      : hasUncoded
                                      ? `${uncoded24h} Uncoded`
                                      : 'Healthy (24h)'}
                                  </Badge>
                                </HStack>
                                <Button
                                  size="xs"
                                  colorScheme="blue"
                                  variant="ghost"
                                  onClick={() => setTabIndex(ORG_TAB_INDEX[key])}
                                >
                                  Org Tab →
                                </Button>
                              </Flex>
                            </CardHeader>

                            <CardBody pt={1} pb={3} px={4}>
                              <SimpleGrid columns={2} spacing={2.5} mb={3}>
                                <Box bg="white" p={2.5} borderRadius="md" border="1px solid" borderColor="gray.200">
                                  <Text fontSize="2xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                                    Cases Created (24h)
                                  </Text>
                                  <HStack align="baseline" spacing={2} mt={0.5}>
                                    <Text fontSize="xl" fontWeight="bold" color="gray.800">
                                      {created24h}
                                    </Text>
                                    {l24.last_case_created_at && (
                                      <Text fontSize="2xs" color="gray.500">
                                        last {formatRelativeTime(l24.last_case_created_at)}
                                      </Text>
                                    )}
                                  </HStack>
                                </Box>

                                <Box bg="white" p={2.5} borderRadius="md" border="1px solid" borderColor="gray.200">
                                  <Text fontSize="2xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                                    Total Coded (24h)
                                  </Text>
                                  <HStack align="baseline" spacing={1.5} mt={0.5} flexWrap="wrap">
                                    <Text fontSize="xl" fontWeight="bold" color="green.600">
                                      {coded24h}
                                    </Text>
                                    <Badge colorScheme={rateScheme24h} fontSize="2xs">
                                      {rate24h}%
                                    </Badge>
                                    {pulse.is_async_coding_org && cptCoded24h > 0 && (
                                      <Badge colorScheme="purple" fontSize="2xs">
                                        {cptCoded24h} CPT
                                      </Badge>
                                    )}
                                    {!pulse.is_async_coding_org && codedAny24h > coded24h && (
                                      <Badge colorScheme="teal" fontSize="2xs">
                                        {codedAny24h} total runs
                                      </Badge>
                                    )}
                                  </HStack>
                                </Box>

                                <Box bg="white" p={2.5} borderRadius="md" border="1px solid" borderColor="gray.200">
                                  <Text fontSize="2xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                                    Uncoded / Gap (24h)
                                  </Text>
                                  <HStack align="baseline" spacing={2} mt={0.5}>
                                    <Text
                                      fontSize="xl"
                                      fontWeight="bold"
                                      color={uncoded24h > 0 ? 'orange.500' : 'green.600'}
                                    >
                                      {uncoded24h}
                                    </Text>
                                    {l24.last_coded_at && (
                                      <Text fontSize="2xs" color="gray.500">
                                        coded {formatRelativeTime(l24.last_coded_at)}
                                      </Text>
                                    )}
                                  </HStack>
                                </Box>

                                <Box
                                  bg="white"
                                  p={2.5}
                                  borderRadius="md"
                                  border="1px solid"
                                  borderColor={hasFailures ? 'red.300' : 'gray.200'}
                                  cursor="pointer"
                                  onClick={() => setTabIndex(1)}
                                >
                                  <Text fontSize="2xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                                    Failures (24h)
                                  </Text>
                                  <HStack align="baseline" spacing={1.5} mt={0.5} flexWrap="wrap">
                                    <Text
                                      fontSize="xl"
                                      fontWeight="bold"
                                      color={hasFailures ? 'red.600' : 'green.600'}
                                    >
                                      {failures24h}
                                    </Text>
                                    {coreFailed24h > 0 && (
                                      <Badge colorScheme="red" fontSize="2xs">
                                        {coreFailed24h} core
                                      </Badge>
                                    )}
                                    {syncFailed24h > 0 && (
                                      <Badge colorScheme="red" fontSize="2xs">
                                        {syncFailed24h} worker
                                      </Badge>
                                    )}
                                    {uploadFailed24h > 0 && (
                                      <Badge colorScheme="red" fontSize="2xs">
                                        {uploadFailed24h} upload
                                      </Badge>
                                    )}
                                    {fileErr24h > 0 && (
                                      <Badge colorScheme="orange" fontSize="2xs">
                                        {fileErr24h} file
                                      </Badge>
                                    )}
                                  </HStack>
                                </Box>
                              </SimpleGrid>

                              <Box bg="white" p={2.5} borderRadius="md" border="1px solid" borderColor="gray.200">
                                <Flex justify="space-between" align="center" mb={1}>
                                  <Text fontSize="2xs" fontWeight="bold" color="gray.600" textTransform="uppercase">
                                    24h Customer Activity ({l24.active_users_count || 0} users)
                                  </Text>
                                  <Badge colorScheme="blue" variant="subtle" fontSize="2xs">
                                    {l24.activity_events_count || 0} events
                                  </Badge>
                                </Flex>
                                {logins24h.length === 0 ? (
                                  <Text fontSize="xs" color="gray.400">
                                    No customer logins in last 24h
                                  </Text>
                                ) : (
                                  <VStack align="stretch" spacing={1}>
                                    {logins24h.map((u) => (
                                      <Flex key={`24h-u-${u.user_id}`} justify="space-between" align="center" fontSize="xs">
                                        <Text fontWeight="medium" color="gray.800" noOfLines={1}>
                                          {u.name}{' '}
                                          <Text as="span" color="gray.400" fontSize="2xs">
                                            ({u.email})
                                          </Text>
                                        </Text>
                                        <Badge colorScheme="green" variant="subtle" fontSize="2xs" textTransform="none">
                                          {formatTimestamp(u.last_login_at)} ({formatRelativeTime(u.last_login_at)})
                                        </Badge>
                                      </Flex>
                                    ))}
                                  </VStack>
                                )}
                              </Box>
                            </CardBody>
                          </Card>
                        );
                      })}
                    </SimpleGrid>
                  </Box>

                  {/* Multi-Day Section Header */}
                  <Flex justify="space-between" align="center" mb={3}>
                    <HStack spacing={2}>
                      <Heading size="sm" color="gray.700">
                        Multi-Day Operational Trend & Rollup
                      </Heading>
                      <Badge colorScheme="purple" variant="subtle" fontSize="2xs">
                        LAST {daysWindow} DAYS
                      </Badge>
                    </HStack>
                  </Flex>

                  {/* Cross-Org Rollup Strip */}
                  <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
                    <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
                      <CardBody py={4}>
                        <Stat>
                          <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                            All Orgs Cases Created ({daysWindow}d)
                          </StatLabel>
                          <StatNumber fontSize="2xl" color="gray.800">{totals.created}</StatNumber>
                          <StatHelpText mb={0} fontSize="xs" color="gray.500">
                            NMA + USNeuro + MPowerHealth
                          </StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>

                    <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
                      <CardBody py={4}>
                        <Stat>
                          <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                            All Orgs Coded ({daysWindow}d)
                          </StatLabel>
                          <HStack align="baseline" spacing={2}>
                            <StatNumber fontSize="2xl" color="green.600">{totals.coded}</StatNumber>
                            <Badge colorScheme={getRateColorScheme(globalCodingRate, totals.created)}>
                              {globalCodingRate}%
                            </Badge>
                          </HStack>
                          <StatHelpText mb={0} fontSize="xs" color="gray.500">
                            Any coding analysis version
                          </StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>

                    <Card
                      shadow="sm"
                      border="1px solid"
                      borderColor={allDiagnostics.length > 0 ? 'red.200' : 'gray.200'}
                      bg="white"
                      cursor="pointer"
                      onClick={() => setTabIndex(1)}
                    >
                      <CardBody py={4}>
                        <Stat>
                          <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                            Uncoded & Pipeline Issues ({daysWindow}d)
                          </StatLabel>
                          <HStack align="baseline" spacing={2}>
                            <StatNumber
                              fontSize="2xl"
                              color={allDiagnostics.length > 0 ? 'red.600' : 'green.600'}
                            >
                              {allDiagnostics.length}
                            </StatNumber>
                            <Badge colorScheme="orange">{totals.uncoded} uncoded</Badge>
                            {totals.syncErrors > 0 && (
                              <Badge colorScheme="red">{totals.syncErrors} sync err</Badge>
                            )}
                          </HStack>
                          <StatHelpText mb={0} fontSize="xs" color="blue.600">
                            Click to open Errors & Uncoded Triage →
                          </StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>

                    <Card
                      shadow="sm"
                      border="1px solid"
                      borderColor="gray.200"
                      bg="white"
                      cursor="pointer"
                      onClick={() => setTabIndex(RECENT_LOGINS_TAB_INDEX)}
                    >
                      <CardBody py={4}>
                        <Stat>
                          <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                            Active Customer Users ({daysWindow}d)
                          </StatLabel>
                          <StatNumber fontSize="2xl" color="blue.600">{totals.users}</StatNumber>
                          <StatHelpText mb={0} fontSize="xs" color="blue.600">
                            Click to view Recent Logins & Activity →
                          </StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  {/* 3-Column Side-by-Side Org Highlights */}
                  <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={5} mb={6}>
                    {orgKeys.map((key) => {
                      const pulse = orgs[key] || {};
                      const s = pulse.summary || {};
                      const daily = pulse.daily_stats || [];
                      const logins = (pulse.recent_logins || []).slice(0, 4);
                      const rateScheme = getRateColorScheme(s.overall_coding_rate || 0, s.total_created || 0);

                      return (
                        <Card key={key} shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
                          <CardHeader pb={2}>
                            <Flex justify="space-between" align="center">
                              <Box>
                                <HStack spacing={2}>
                                  <Heading size="sm" color="gray.800">
                                    {pulse.org_name || key}
                                  </Heading>
                                  <Badge colorScheme="gray" fontSize="2xs">Org {pulse.org_id}</Badge>
                                </HStack>
                                <Text fontSize="2xs" color="gray.500" mt={0.5}>
                                  {s.total_created || 0} created • {s.total_coded || 0} coded • {s.active_users_count || 0} active users
                                </Text>
                              </Box>
                              <Button
                                size="xs"
                                colorScheme="blue"
                                variant="outline"
                                onClick={() => setTabIndex(ORG_TAB_INDEX[key])}
                              >
                                Full View →
                              </Button>
                            </Flex>
                          </CardHeader>

                          <CardBody pt={2}>
                            <HStack spacing={2} mb={3} flexWrap="wrap">
                              <Badge colorScheme={rateScheme}>
                                Coding: {s.overall_coding_rate || 0}%
                              </Badge>
                              <Badge colorScheme={(s.total_uncoded || 0) > 0 ? 'orange' : 'green'}>
                                Uncoded: {s.total_uncoded || 0}
                              </Badge>
                              {pulse.is_async_coding_org && s.total_cpt_coded > 0 && (
                                <Badge colorScheme="purple">
                                  CPT Coded: {s.total_cpt_coded}
                                </Badge>
                              )}
                              {(s.sync_failed_count > 0 || s.upload_failed_count > 0) && (
                                <Badge colorScheme="red">
                                  Worker/Upload Err: {(s.sync_failed_count || 0) + (s.upload_failed_count || 0)}
                                </Badge>
                              )}
                            </HStack>

                            {/* Daily Trend Mini-Table */}
                            <Table size="sm" variant="simple" mb={3}>
                              <Thead bg="gray.50">
                                <Tr>
                                  <Th fontSize="2xs" py={1.5}>Date</Th>
                                  <Th fontSize="2xs" py={1.5} isNumeric>New</Th>
                                  <Th fontSize="2xs" py={1.5} isNumeric>Coded</Th>
                                  <Th fontSize="2xs" py={1.5} isNumeric>Gap</Th>
                                  <Th fontSize="2xs" py={1.5}>Rate</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {daily.slice(0, 5).map((d) => (
                                  <Tr key={d.date}>
                                    <Td fontSize="xs" py={1.5}>{formatShortDate(d.date)}</Td>
                                    <Td fontSize="xs" py={1.5} isNumeric fontWeight="bold">{d.total_created}</Td>
                                    <Td fontSize="xs" py={1.5} isNumeric color="green.600">{d.coded_count}</Td>
                                    <Td
                                      fontSize="xs"
                                      py={1.5}
                                      isNumeric
                                      color={d.uncoded_count > 0 ? 'orange.600' : 'gray.400'}
                                      fontWeight={d.uncoded_count > 0 ? 'bold' : 'normal'}
                                    >
                                      {d.uncoded_count}
                                    </Td>
                                    <Td py={1.5}>
                                      {d.total_created > 0 ? (
                                        <Progress
                                          value={d.coding_rate}
                                          size="xs"
                                          colorScheme={getRateColorScheme(d.coding_rate, d.total_created)}
                                          borderRadius="full"
                                        />
                                      ) : (
                                        <Text fontSize="2xs" color="gray.400">—</Text>
                                      )}
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>

                            <Divider mb={2} />
                            <Text fontSize="2xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={1.5}>
                              Recent Customer Logins ({s.active_users_count || 0})
                            </Text>
                            {logins.length === 0 ? (
                              <Text fontSize="xs" color="gray.400">No recent customer logins.</Text>
                            ) : (
                              <VStack align="stretch" spacing={1}>
                                {logins.map((u) => (
                                  <Flex key={u.user_id} justify="space-between" align="center" fontSize="xs">
                                    <Text fontWeight="medium" color="gray.800" noOfLines={1}>
                                      {u.name} <Text as="span" color="gray.400" fontSize="2xs">({u.email})</Text>
                                    </Text>
                                    <Badge colorScheme="green" variant="subtle" fontSize="2xs" textTransform="none">
                                      {formatTimestamp(u.last_login_at)} ({formatRelativeTime(u.last_login_at)})
                                    </Badge>
                                  </Flex>
                                ))}
                              </VStack>
                            )}
                          </CardBody>
                        </Card>
                      );
                    })}
                  </SimpleGrid>

                  {/* Unified Errors & Uncoded Triage on Main Overview */}
                  <SuperAdminErrorsTriagePanel diagnostics={allDiagnostics} initialOrgFilter="ALL" />
                </>
              )}
            </TabPanel>

            {/* Tab 1: Dedicated Cross-Org Errors & Uncoded Triage */}
            <TabPanel px={0} pt={6}>
              <SuperAdminErrorsTriagePanel diagnostics={allDiagnostics} initialOrgFilter="ALL" />
            </TabPanel>

            {/* Tab 2: NMA (Org 1) */}
            <TabPanel px={0} pt={6}>
              <SuperAdminOrgPulseView orgKey="NMA" orgPulse={orgs.NMA} daysWindow={daysWindow} />
            </TabPanel>

            {/* Tab 3: USNeuro (Org 155) */}
            <TabPanel px={0} pt={6}>
              <SuperAdminOrgPulseView orgKey="USN" orgPulse={orgs.USN} daysWindow={daysWindow} />
            </TabPanel>

            {/* Tab 4: MPowerHealth (Org 203) */}
            <TabPanel px={0} pt={6}>
              <SuperAdminOrgPulseView orgKey="MPH" orgPulse={orgs.MPH} daysWindow={daysWindow} />
            </TabPanel>

            {/* Tab 5: All Recent Logins & Detailed User Activity */}
            <TabPanel px={0} pt={6}>
              <SuperAdminUserActivityPanel daysWindow={daysWindow} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
};

export default SuperAdminDashboard;
